import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { CheckCircleIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useDeviceId } from '@/hooks/useDeviceId';
import { SALES_MODULES } from '@/components/sales';
import api from '@/utils/ky';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';

interface CategoryProps {
  category: any;
  isLocked: boolean;
  onClick: () => void;
}

const CategoryCard = ({ category, isLocked, onClick }: CategoryProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={cn(
      'relative group bg-white p-6 rounded-xl shadow-sm transition-all duration-200',
      !isLocked && 'hover:shadow-md cursor-pointer hover:scale-[1.02]',
      isLocked && 'opacity-75'
    )}
    onClick={() => !isLocked && onClick()}
  >
    <div className="flex items-start justify-between">
      <div>
        <span className="text-4xl mb-4 block">{category.icon}</span>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {category.title}
        </h3>
        <p className="text-sm text-gray-500">
          {category.description}
        </p>
      </div>

      {isLocked ? (
        <div className="text-gray-400">
          <LockClosedIcon className="h-5 w-5" />
        </div>
      ) : (
        <div className="text-blue-600">
          <CheckCircleIcon className="h-5 w-5" />
        </div>
      )}
    </div>
  </motion.div>
);

const NameDialog = ({ isOpen, onClose, onSubmit }) => {
  const [name, setName] = useState('');

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30 backdrop-blur" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm rounded-xl bg-white p-8 shadow-xl">
          <Dialog.Title className="text-xl font-semibold mb-4">
            Welcome! Let's get started
          </Dialog.Title>
          <p className="text-gray-600 text-sm mb-6">
            Enter your name to begin your personalized sales training
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="mb-6"
          />
          <Button
            onClick={() => onSubmit(name)}
            disabled={!name.trim()}
            className="w-full"
          >
            Continue to Training
          </Button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default function SalesCategories() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isNameDialogOpen, setNameDialogOpen] = useState(false);
  const deviceId = useDeviceId();

  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', deviceId],
    queryFn: async () => {
      try {
        const response = await api
          .get('api/sales/users', {
            searchParams: { deviceId },
          })
          .json<{ userId: string; name: string; company: string }>();

        if (!response || !response.userId) {
          return null;
        }

        localStorage.setItem('user_name', response.name);
        localStorage.setItem('user_company', response.company);

        return response;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!deviceId,
    retry: false,
  });

  const { mutate: createUser, isPending: isCreatingUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
      code: string;
    }) =>
      api
        .post('api/sales/users', {
          json: data,
        })
        .json<{
          userId: string;
          deviceId: string;
          companyName: string;
        }>(),
    onSuccess: (response, variables) => {
      localStorage.setItem('user_name', variables.name);
      localStorage.setItem('user_company', response.companyName);

      posthog.capture('user_created', {
        deviceId: variables.deviceId,
        code: variables.code,
      });

      navigateToChat(variables.name, response.companyName, selectedCategory);
    },
    onError: () => {
      toast.error('Failed to create user. Please try again.');
    },
  });

  const navigateToChat = (userName: string, companyName: string, categoryId: string) => {
    const pathToChat = '/sales/chat?code=' + searchParams.get('code');
    navigate(pathToChat, {
      state: {
        userName,
        category: categoryId,
        companyName,
      },
    });
  };

  const handleNameSubmit = (name: string) => {
    setNameDialogOpen(false);
    if (deviceId) {
      createUser({
        name,
        deviceId,
        function: 'sales',
        code: searchParams.get('code') || '',
      });
    }
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category.id);
    if (userData?.name) {
      navigateToChat(userData.name, userData.company, category.id);
    } else {
      setNameDialogOpen(true);
    }
  };

  if (!deviceId || isLoadingUser || isCreatingUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 py-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-4">
            Choose Your Focus Area
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select a training module to practice specific sales skills and scenarios
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {SALES_MODULES.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              isLocked={category.locked}
              onClick={() => handleCategorySelect(category)}
            />
          ))}
        </div>

        {/* Progress Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Practice Sessions</h3>
            <p className="text-3xl font-semibold text-gray-900">24</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Skills Improved</h3>
            <p className="text-3xl font-semibold text-gray-900">7</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Time Practiced</h3>
            <p className="text-3xl font-semibold text-gray-900">3.5h</p>
          </div>
        </div>
      </div>

      <NameDialog
        isOpen={isNameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        onSubmit={handleNameSubmit}
      />
    </div>
  );
}